import React from "react";
import { connect } from "react-redux";
import moment from "moment";
import { getStorage } from "../../utils/storage";
import api from "../../utils/api";
import { setUnreadCount, markAllRead } from "../../actions/InAppNotification";
import { Loading } from "../Loader";
import "./styles.css";

const API_URL = process.env.REACT_APP_BBMS_BASE_API;

const resolveIcon = (notif) => {
  const text =
    ((notif.title || "") + " " + (notif.body || "") + " " + (notif.targetScreen || "")).toLowerCase();

  if (text.includes("request") || text.includes("blood-request"))
    return { icon: "water_drop", color: "#c0392b", bg: "#fdecea" };
  if (text.includes("transfer") || text.includes("incoming") || text.includes("shipment"))
    return { icon: "local_shipping", color: "#e67e22", bg: "#fef3e2" };
  if (text.includes("received") || text.includes("pickup") || text.includes("ready"))
    return { icon: "check_circle", color: "#27ae60", bg: "#eafaf1" };
  if (text.includes("return"))
    return { icon: "undo", color: "#2980b9", bg: "#eaf4fb" };
  if (text.includes("donat") || text.includes("thank"))
    return { icon: "favorite", color: "#e74c3c", bg: "#fdedec" };
  if (text.includes("password") || text.includes("reset"))
    return { icon: "lock_reset", color: "#e67e22", bg: "#fef3e2" };
  if (text.includes("workspace") || text.includes("switched") || text.includes("blood bank"))
    return { icon: "swap_horiz", color: "#8e44ad", bg: "#f5eef8" };
  if (text.includes("block"))
    return { icon: "block", color: "#7f8c8d", bg: "#f0f3f4" };
  if (text.includes("donor") || text.includes("person"))
    return { icon: "person", color: "#1877f2", bg: "#e7f3ff" };
  return { icon: "notifications", color: "#8e44ad", bg: "#f5eef8" };
};

const normalizeBody = (body) =>
  body?.replace(/\bblood bank\b/gi, "workspace") || body;

const extractWorkspaceName = (body) => {
  const m = body?.match(/"([^"]+)"/);
  return m ? m[1] : null;
};

// Build stockName → hospitalName lookup from user session hospitals array
const buildHospitalMap = (hospitals = []) => {
  const map = {};
  hospitals.forEach((h) => {
    const stockKey = h?.bloodBank?.name;
    if (stockKey && h?.name) map[stockKey] = h.name;
  });
  return map;
};

// Replace stock names in body text with hospital names
const applyHospitalMap = (body, hospitalMap) => {
  if (!body || !Object.keys(hospitalMap).length) return body;
  let result = body;
  Object.entries(hospitalMap).forEach(([stock, hospital]) => {
    result = result.replace(new RegExp(`"${stock}"`, "g"), `"${hospital}"`);
  });
  return result;
};

const resolveDisplayName = (body, hospitalMap) => {
  const extracted = extractWorkspaceName(normalizeBody(applyHospitalMap(body, hospitalMap)));
  if (!extracted) return null;
  return extracted;
};

const HIGHLIGHT_PATTERNS = [
  /\b(A[Bb]?[+-]|[BObo][+-])\b/g,
  /"([^"]+)"/g,
  /\b(urgent|critical|emergency|attention|required|immediately|alert)\b/gi,
  /\b(\d+)\s*(unit[s]?|bag[s]?|pack[s]?|ml|cc)\b/g,
];

const highlightBody = (text) => {
  if (!text) return null;
  const marks = [];

  HIGHLIGHT_PATTERNS.forEach((pattern) => {
    const re = new RegExp(pattern.source, pattern.flags.includes("g") ? pattern.flags : pattern.flags + "g");
    let m;
    re.lastIndex = 0;
    while ((m = re.exec(text)) !== null) {
      if (m[0].trim()) marks.push({ start: m.index, end: m.index + m[0].length });
    }
  });

  marks.sort((a, b) => a.start - b.start);
  const merged = [];
  for (const mark of marks) {
    if (merged.length && mark.start <= merged[merged.length - 1].end) {
      merged[merged.length - 1].end = Math.max(merged[merged.length - 1].end, mark.end);
    } else {
      merged.push({ ...mark });
    }
  }

  const segments = [];
  let cursor = 0;
  merged.forEach(({ start, end }, i) => {
    if (cursor < start) segments.push(<span key={`p${i}`}>{text.slice(cursor, start)}</span>);
    segments.push(
      <mark key={`h${i}`} className="notif-highlight">{text.slice(start, end)}</mark>
    );
    cursor = end;
  });
  if (cursor < text.length) segments.push(<span key="tail">{text.slice(cursor)}</span>);
  return segments.length ? segments : text;
};

const groupNotifications = (items) => {
  const startOfToday = moment().startOf("day");
  const startOfYesterday = moment().subtract(1, "day").startOf("day");
  const startOfWeek = moment().subtract(7, "day").startOf("day");

  const groups = [
    { label: "Today", items: [] },
    { label: "Yesterday", items: [] },
    { label: "This week", items: [] },
    { label: "Earlier", items: [] },
  ];

  items.forEach((n) => {
    const t = moment(n.createdAt);
    if (t.isSameOrAfter(startOfToday)) groups[0].items.push(n);
    else if (t.isSameOrAfter(startOfYesterday)) groups[1].items.push(n);
    else if (t.isSameOrAfter(startOfWeek)) groups[2].items.push(n);
    else groups[3].items.push(n);
  });

  return groups.filter((g) => g.items.length > 0);
};

class Notifications extends React.Component {
  state = {
    isLoading: true,
    notifications: [],
    page: 1,
    limit: 10,
    hasMore: true,
    markingRead: false,
    tab: "all",
    selectedId: null,
    deleteConfirm: null,
    hospitalMap: {},
    isSelecting: false,
    checkedIds: [],
    bulkDeleteConfirm: false,
    receipts: [],
    receiptsLoading: false,
  };

  componentDidMount() {
    this.loadHospitalMap();
    this.fetchNotifications(true);
  }

  loadHospitalMap = async () => {
    try {
      const user = await getStorage();
      // hospitals may be in session or need a fresh fetch
      let hospitals = user?.hospitals || [];
      if (!hospitals.length && user?.id && user?.token) {
        const res = await api.get(`${API_URL}/account`, {
          params: { id: user.id },
          headers: { Authorization: `Bearer ${user.token}` },
        });
        const accounts = Array.isArray(res.data) ? res.data : res.data?.data || [];
        hospitals = accounts?.[0]?.hospitals || [];
      }
      this.setState({ hospitalMap: buildHospitalMap(hospitals) });
    } catch { /* fall back to empty map */ }
  };

  componentDidUpdate(prevProps) {
    if (
      prevProps.latestNotification !== this.props.latestNotification &&
      this.props.latestNotification
    ) {
      this.setState((prev) => ({
        notifications: [this.props.latestNotification, ...prev.notifications],
      }));
    }
  }

  fetchNotifications = async (reset = false) => {
    try {
      const user = await getStorage();
      if (!user?.id) return;
      const page = reset ? 1 : this.state.page;
      this.setState({ isLoading: true });
      const res = await api.get(`${API_URL}/notification`, {
        params: { account: user.id, page, limit: this.state.limit },
      });
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      this.setState((prev) => ({
        isLoading: false,
        notifications: reset ? data : [...prev.notifications, ...data],
        page: page + 1,
        hasMore: data.length === this.state.limit,
      }));
      const allNotifs = reset ? data : [...this.state.notifications, ...data];
      this.props.dispatch(setUnreadCount(allNotifs.filter((n) => !n.viewed).length));
    } catch {
      this.setState({ isLoading: false });
    }
  };

  handleMarkAllRead = async () => {
    try {
      const user = await getStorage();
      if (!user?.id) return;
      this.setState({ markingRead: true });
      await api.post(`${API_URL}/notification/mark-all-read`, { account: user.id });
      this.setState((prev) => ({
        markingRead: false,
        notifications: prev.notifications.map((n) => ({ ...n, viewed: true })),
      }));
      this.props.dispatch(markAllRead());
    } catch {
      this.setState({ markingRead: false });
    }
  };

  handleMarkRead = async (notifId) => {
    try {
      await api.put(`${API_URL}/notification`, { id: notifId, viewed: true });
      this.setState((prev) => {
        const updated = prev.notifications.map((n) =>
          n._id === notifId ? { ...n, viewed: true } : n
        );
        this.props.dispatch(setUnreadCount(updated.filter((n) => !n.viewed).length));
        return { notifications: updated };
      });
    } catch { /* ignore */ }
  };

  selectNotif = (notif) => {
    this.setState({ selectedId: notif._id, receipts: [], receiptsLoading: false });
    if (!notif.viewed) this.handleMarkRead(notif._id);
    if (notif.broadcastId) this.fetchReceipts(notif._id);
  };

  fetchReceipts = async (notifId) => {
    this.setState({ receiptsLoading: true });
    try {
      const res = await api.get(`${API_URL}/notification/receipts`, {
        params: { notificationId: notifId },
      });
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      this.setState({ receipts: data, receiptsLoading: false });
    } catch {
      this.setState({ receipts: [], receiptsLoading: false });
    }
  };

  goBackToList = () => this.setState({ selectedId: null });

  requestDelete = (e, notif) => {
    e.stopPropagation();
    this.setState({ deleteConfirm: notif });
  };

  confirmDelete = async () => {
    const { deleteConfirm } = this.state;
    if (!deleteConfirm) return;
    try {
      await api.delete(`${API_URL}/notification`, { data: { id: deleteConfirm._id } });
      this.setState((prev) => {
        const updated = prev.notifications.filter((n) => n._id !== deleteConfirm._id);
        this.props.dispatch(setUnreadCount(updated.filter((n) => !n.viewed).length));
        return {
          notifications: updated,
          deleteConfirm: null,
          selectedId: prev.selectedId === deleteConfirm._id ? null : prev.selectedId,
        };
      });
    } catch {
      this.setState({ deleteConfirm: null });
    }
  };

  cancelDelete = () => this.setState({ deleteConfirm: null });

  toggleSelectMode = () => {
    this.setState((prev) => ({
      isSelecting: !prev.isSelecting,
      checkedIds: [],
      selectedId: null,
    }));
  };

  toggleCheck = (e, id) => {
    e.stopPropagation();
    this.setState((prev) => ({
      checkedIds: prev.checkedIds.includes(id)
        ? prev.checkedIds.filter((c) => c !== id)
        : [...prev.checkedIds, id],
    }));
  };

  toggleSelectAll = () => {
    const { tab, notifications, checkedIds } = this.state;
    const filtered = tab === "unread" ? notifications.filter((n) => !n.viewed) : notifications;
    const allIds = filtered.map((n) => n._id);
    const allSelected = allIds.length > 0 && allIds.every((id) => checkedIds.includes(id));
    this.setState({ checkedIds: allSelected ? [] : allIds });
  };

  confirmBulkDelete = async () => {
    const { checkedIds } = this.state;
    if (!checkedIds.length) return;
    try {
      await Promise.all(
        checkedIds.map((id) => api.delete(`${API_URL}/notification`, { data: { id } }))
      );
      this.setState((prev) => {
        const updated = prev.notifications.filter((n) => !checkedIds.includes(n._id));
        this.props.dispatch(setUnreadCount(updated.filter((n) => !n.viewed).length));
        return {
          notifications: updated,
          checkedIds: [],
          isSelecting: false,
          bulkDeleteConfirm: false,
          selectedId: null,
        };
      });
    } catch {
      this.setState({ bulkDeleteConfirm: false });
    }
  };

  renderDeleteConfirmModal = () => {
    const { deleteConfirm } = this.state;
    if (!deleteConfirm) return null;

    return (
      <div className="notif-modal-overlay" onClick={this.cancelDelete}>
        <div className="notif-modal notif-confirm-modal" onClick={(e) => e.stopPropagation()}>
          <div className="notif-confirm-modal__title-row">
            <div className="notif-confirm-modal__icon">
              <span className="material-symbols-outlined" style={{ fontSize: 20, color: "var(--primary-color)", display: "flex" }}>
                delete
              </span>
            </div>
            <span className="notif-confirm-modal__title">Delete notification?</span>
          </div>
          {deleteConfirm.title && (
            <p className="notif-confirm-modal__notif-name">"{deleteConfirm.title}"</p>
          )}
          <p className="notif-confirm-modal__body">
            This notification will be permanently removed.
          </p>
          <div className="notif-confirm-modal__actions">
            <button className="notif-btn notif-btn--ghost" onClick={this.cancelDelete}>
              Cancel
            </button>
            <button className="notif-btn notif-btn--danger" onClick={this.confirmDelete}>
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  renderBulkDeleteModal = () => {
    const { bulkDeleteConfirm, checkedIds } = this.state;
    if (!bulkDeleteConfirm) return null;
    const count = checkedIds.length;
    return (
      <div className="notif-modal-overlay" onClick={() => this.setState({ bulkDeleteConfirm: false })}>
        <div className="notif-modal notif-confirm-modal" onClick={(e) => e.stopPropagation()}>
          <div className="notif-confirm-modal__title-row">
            <div className="notif-confirm-modal__icon">
              <span className="material-symbols-outlined" style={{ fontSize: 20, color: "var(--primary-color)", display: "flex" }}>
                delete_sweep
              </span>
            </div>
            <span className="notif-confirm-modal__title">
              Delete {count} notification{count !== 1 ? "s" : ""}?
            </span>
          </div>
          <p className="notif-confirm-modal__body">
            {count} notification{count !== 1 ? "s" : ""} will be permanently removed. This cannot be undone.
          </p>
          <div className="notif-confirm-modal__actions">
            <button className="notif-btn notif-btn--ghost" onClick={() => this.setState({ bulkDeleteConfirm: false })}>
              Cancel
            </button>
            <button className="notif-btn notif-btn--danger" onClick={this.confirmBulkDelete}>
              <span className="material-symbols-outlined" style={{ fontSize: 15 }}>delete</span>
              Delete {count}
            </button>
          </div>
        </div>
      </div>
    );
  };

  renderListItem = (notif) => {
    const isUnread = !notif.viewed;
    const isSelected = this.state.selectedId === notif._id;
    const { isSelecting, checkedIds } = this.state;
    const isChecked = checkedIds.includes(notif._id);
    const { icon, color, bg } = resolveIcon(notif);
    const workspaceName = resolveDisplayName(notif.body, this.state.hospitalMap);

    return (
      <div
        key={notif._id}
        className={`notif-list-item${isUnread ? " notif-list-item--unread" : ""}${isSelected ? " notif-list-item--selected" : ""}${isChecked ? " notif-list-item--checked" : ""}`}
        onClick={isSelecting ? (e) => this.toggleCheck(e, notif._id) : () => this.selectNotif(notif)}
      >
        {isSelecting && (
          <div className="notif-list-item__checkbox">
            <input
              type="checkbox"
              checked={isChecked}
              onChange={(e) => this.toggleCheck(e, notif._id)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        <div className="notif-list-item__icon" style={{ background: bg }}>
          <span className="material-symbols-outlined" style={{ color, fontSize: 18 }}>
            {icon}
          </span>
        </div>

        <div className="notif-list-item__content">
          <p className="notif-list-item__title">{notif.title || "Notification"}</p>
          {workspaceName ? (
            <span className="notif-list-item__workspace">{workspaceName}</span>
          ) : (
            <p className="notif-list-item__preview">{normalizeBody(notif.body)}</p>
          )}
          <span className={`notif-list-item__time${isUnread ? " notif-list-item__time--new" : ""}`}>
            {moment(notif.createdAt).fromNow()}
          </span>
        </div>

        <div className="notif-list-item__meta">
          {isUnread && !isSelecting && <span className="notif-list-item__badge" />}
          {!isSelecting && (
            <button
              className="notif-list-item__delete"
              onClick={(e) => this.requestDelete(e, notif)}
              title="Remove notification"
            >
              <span className="material-symbols-outlined" style={{ fontSize: 15 }}>delete</span>
            </button>
          )}
        </div>
      </div>
    );
  };

  renderReceipts = (receipts, loading) => {
    const seen = receipts.filter((r) => r.viewed);
    const total = receipts.length;
    const pct = total > 0 ? Math.round((seen.length / total) * 100) : 0;

    return (
      <div className="notif-receipts">
        <div className="notif-receipts__header">
          <span
            className="material-symbols-outlined"
            style={{ fontSize: 15, color: "var(--text-color-60)", display: "flex" }}
          >
            done_all
          </span>
          <span className="notif-receipts__title">Seen by</span>
          {!loading && total > 0 && (
            <span className="notif-receipts__count">
              {seen.length} / {total}
            </span>
          )}
        </div>

        {!loading && total > 0 && (
          <div className="notif-receipts__progress">
            <div
              className="notif-receipts__progress-bar"
              style={{ width: `${pct}%` }}
            />
          </div>
        )}

        {loading ? (
          <p className="notif-receipts__empty">Loading…</p>
        ) : seen.length === 0 ? (
          <p className="notif-receipts__empty">No one has seen this yet</p>
        ) : (
          <div className="notif-receipts__list">
            {seen.map((r, i) => (
              <div key={i} className="notif-receipt-item">
                <div className="notif-receipt-item__avatar">
                  {(r.user?.displayName || "?").charAt(0).toUpperCase()}
                </div>
                <span className="notif-receipt-item__name">
                  {r.user?.displayName || "Unknown"}
                </span>
                {r.viewedAt && (
                  <span className="notif-receipt-item__time">
                    {moment(r.viewedAt).fromNow()}
                  </span>
                )}
              </div>
            ))}
            {total > seen.length && (
              <p className="notif-receipts__pending">
                {total - seen.length} recipient{total - seen.length !== 1 ? "s" : ""} haven't seen this yet
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  renderDetail = () => {
    const { selectedId, notifications, receipts, receiptsLoading } = this.state;
    const notif = notifications.find((n) => n._id === selectedId);

    if (!notif) {
      return (
        <div className="notif-detail-area notif-detail-area--empty">
          <span className="material-symbols-outlined notif-detail-empty__icon">
            mark_email_read
          </span>
          <p className="notif-detail-empty__title">No notification selected</p>
          <p className="notif-detail-empty__sub">Click a notification from the list to read it</p>
        </div>
      );
    }

    const { icon, color, bg } = resolveIcon(notif);
    const workspaceName = resolveDisplayName(notif.body, this.state.hospitalMap);
    const isUnread = !notif.viewed;

    return (
      <div className="notif-detail-area">
        {/* Mobile back button */}
        <button className="notif-back-btn" onClick={this.goBackToList}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_back</span>
          Back
        </button>

        <div className="notif-detail-card">
          <div className="notif-detail-card__header">
            <div className="notif-detail-card__icon" style={{ background: bg }}>
              <span className="material-symbols-outlined" style={{ color, fontSize: 20 }}>
                {icon}
              </span>
            </div>
            <div className="notif-detail-card__heading">
              <p className="notif-detail-card__title">{notif.title || "Notification"}</p>
              <span className="notif-detail-card__time">
                {moment(notif.createdAt).format("MMM D, YYYY [at] h:mm A")}
                {" · "}
                {moment(notif.createdAt).fromNow()}
              </span>
            </div>
            {isUnread && <span className="notif-detail-card__unread-dot" />}
            <div className="notif-detail-card__header-actions">
              <button
                className="notif-detail-header-btn notif-detail-header-btn--danger"
                onClick={(e) => this.requestDelete(e, notif)}
                title="Delete notification"
              >
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>delete</span>
              </button>
              <button
                className="notif-detail-header-btn"
                onClick={this.goBackToList}
                title="Close"
              >
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>close</span>
              </button>
            </div>
          </div>

          <div className="notif-detail-card__body">
            {workspaceName && (
              <div className="notif-detail-card__workspace-row">
                <span className="notif-detail-card__workspace-label">Hospital / Workspace:</span>
                <span className="notif-detail-card__workspace-name">{workspaceName}</span>
              </div>
            )}
            <p className="notif-detail-card__message">
              {highlightBody(normalizeBody(applyHospitalMap(notif.body, this.state.hospitalMap)))}
            </p>
          </div>

          {notif.broadcastId && this.renderReceipts(receipts, receiptsLoading)}

        </div>
      </div>
    );
  };

  render() {
    const { isLoading, notifications, hasMore, markingRead, tab, selectedId, isSelecting, checkedIds } = this.state;

    const filtered =
      tab === "unread" ? notifications.filter((n) => !n.viewed) : notifications;

    const unreadCount = notifications.filter((n) => !n.viewed).length;
    const hasUnread = unreadCount > 0;
    const groups = groupNotifications(filtered);
    const allSelected =
      filtered.length > 0 && filtered.every((n) => checkedIds.includes(n._id));

    return (
      <div className={`notif-page${selectedId ? " notif-page--detail-open" : ""}`}>
        {/* ── Left panel ── */}
        <div className="notif-panel">
          <div className="notif-panel__header">
            {isSelecting ? (
              <>
                <span className="notif-select-count">
                  {checkedIds.length} selected
                </span>
                <div className="notif-select-actions">
                  <button className="notif-select-all-btn" onClick={this.toggleSelectAll}>
                    {allSelected ? "Deselect all" : "Select all"}
                  </button>
                  <button
                    className="notif-btn notif-btn--danger"
                    disabled={checkedIds.length === 0}
                    onClick={() => this.setState({ bulkDeleteConfirm: true })}
                    style={{ padding: "4px 10px", fontSize: "var(--font-size-xsm)" }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 14 }}>delete</span>
                    Delete{checkedIds.length > 0 ? ` (${checkedIds.length})` : ""}
                  </button>
                  <button className="notif-btn notif-btn--ghost" onClick={this.toggleSelectMode}
                    style={{ padding: "4px 10px", fontSize: "var(--font-size-xsm)" }}>
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="notif-page__tabs">
                  <button
                    className={`notif-tab${tab === "all" ? " notif-tab--active" : ""}`}
                    onClick={() => this.setState({ tab: "all" })}
                  >
                    All
                  </button>
                  <button
                    className={`notif-tab${tab === "unread" ? " notif-tab--active" : ""}`}
                    onClick={() => this.setState({ tab: "unread" })}
                  >
                    Unread
                    {unreadCount > 0 && (
                      <span className="notif-tab__badge">{unreadCount > 99 ? "99+" : unreadCount}</span>
                    )}
                  </button>
                </div>
                <div className="notif-panel__header-actions">
                  {hasUnread && (
                    <button
                      className="notif-mark-all-btn"
                      onClick={this.handleMarkAllRead}
                      disabled={markingRead}
                    >
                      {markingRead ? "Marking…" : "Mark all read"}
                    </button>
                  )}
                  <button
                    className="notif-select-mode-btn"
                    onClick={this.toggleSelectMode}
                    title="Select notifications"
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>checklist</span>
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="notif-panel__list">
            {isLoading && notifications.length === 0 ? (
              <div className="notif-loading"><Loading /></div>
            ) : filtered.length === 0 ? (
              <div className="notif-empty">
                <span className="material-symbols-outlined notif-empty__icon">
                  notifications_none
                </span>
                <p className="notif-empty__title">
                  {tab === "unread" ? "You're all caught up!" : "No notifications yet"}
                </p>
                <p className="notif-empty__sub">
                  {tab === "unread"
                    ? "No unread notifications at this time."
                    : "Notifications about blood requests, donations, and transfers will appear here."}
                </p>
              </div>
            ) : (
              <>
                {groups.map((group) => (
                  <div key={group.label} className="notif-group">
                    <div className="notif-group__label">{group.label}</div>
                    {group.items.map((n) => this.renderListItem(n))}
                  </div>
                ))}
                {hasMore && !isLoading && (
                  <button className="notif-load-more" onClick={() => this.fetchNotifications(false)}>
                    See previous notifications
                  </button>
                )}
                {isLoading && (
                  <div className="notif-loading notif-loading--inline"><Loading /></div>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── Right detail panel ── */}
        {this.renderDetail()}

        {this.renderDeleteConfirmModal()}
        {this.renderBulkDeleteModal()}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  latestNotification: state.InAppNotification?.latestNotification,
});

export default connect(mapStateToProps)(Notifications);
